import axios from 'axios';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://asia-southeast1-catalinx.cloudfunctions.net/weddingFunctions';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle auth errors globally
// Only 401 triggers logout - 403 is for permission denied (not auth failure)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 Unauthorized as auth failure
    // 403 Forbidden is now used for permission issues, not auth
    if (error.response?.status === 401) {
      console.warn('API: 401 Unauthorized - clearing token and redirecting to login');
      localStorage.removeItem('weddingToken');
      
      // Dispatch custom event for App.js to handle re-authentication
      window.dispatchEvent(new CustomEvent('auth-error', { 
        detail: { status: 401, message: error.response?.data || 'Unauthorized' }
      }));
    }
    
    return Promise.reject(error);
  }
);

// React Query hooks for data fetching
// New hook using /media endpoint with cursor-based pagination and type filtering
export const useMediaInfinite = (pageSize = 50, type = 'all', options = {}) => {
  return useInfiniteQuery({
    queryKey: ['media', 'infinite', pageSize, type],
    queryFn: async ({ pageParam = null }) => {
      const token = localStorage.getItem('weddingToken');
      const params = {
        type,
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      const response = await api.get('/media', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 30 * 1000, // 30 seconds - quick refresh for new uploads
    gcTime: 10 * 60 * 1000, // 10 minutes cache - reasonable memory usage
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Legacy hook for backward compatibility (uses /photos endpoint)
export const usePhotosInfinite = (pageSize = 50, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['photos', 'infinite', pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.get('/photos', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageParam, limit: pageSize },
      });
      return {
        photos: response.data.photos,
        pagination: response.data.pagination,
        nextPage: response.data.pagination.hasMore ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 30 * 1000, // 30 seconds - quick refresh for new uploads
    gcTime: 10 * 60 * 1000, // 10 minutes cache - reasonable memory usage
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Keep the old hook for backward compatibility (loads all photos at once)
export const useGuests = (options = {}) => {
  return useQuery({
    queryKey: ['guests'],
    queryFn: async () => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.get('/guests', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute - guest data changes occasionally
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    ...options,
  });
};

export const useAttendanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guestName, tableNumber, attended }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/attendance', {
        guestName,
        tableNumber,
        attended,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update the guests cache and ensure fresh data
      queryClient.setQueryData(['guests'], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map(guest => {
          const guestName = guest.name || '';
          const tableNumber = guest.table || '';
          // Match guests using the same logic as the backend (includes for name, exact match for table)
          if (guestName.toLowerCase().includes(variables.guestName.toLowerCase()) &&
              tableNumber.toString() === variables.tableNumber.toString()) {
            return { ...guest, attended: variables.attended ? 'Y' : 'N' };
          }
          return guest;
        });
      });
      // Invalidate and force refetch to ensure we get the latest data from server immediately
      queryClient.invalidateQueries(['guests'], { refetchType: 'active' });
    },
    onError: (error, variables) => {
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['guests']);
    },
  });
};

// Guestbook API hooks
export const useGuestbookInfinite = (pageSize = 50, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['guestbook', 'infinite', pageSize],
    queryFn: async ({ pageParam = null }) => {
      const token = localStorage.getItem('weddingToken');
      const params = {
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      const response = await api.get('/guestbook', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 0, // Always fetch latest results - no caching
    gcTime: 0, // Don't keep in cache
    refetchOnWindowFocus: true, // Refetch when window regains focus to get latest
    ...options,
  });
};

export const useCreateGuestbookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, message }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/guestbook', {
        name,
        message,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Optimistically add the new message to the cache
      queryClient.setQueryData(['guestbook', 'infinite', 50], (oldData) => {
        if (!oldData) {
          // If no cache exists, create a new one with the message
          return {
            pages: [{
              items: [data],
              nextCursor: null,
              hasMore: false,
            }],
            pageParams: [null],
          };
        }

        // Add the new message to the first page (newest messages appear first)
        const firstPage = oldData.pages[0];
        const updatedFirstPage = {
          ...firstPage,
          items: [data, ...firstPage.items],
        };

        return {
          ...oldData,
          pages: [updatedFirstPage, ...oldData.pages.slice(1)],
        };
      });

      // Invalidate and refetch to ensure consistency with server
      queryClient.invalidateQueries(['guestbook'], { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to create guestbook entry:', error);
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['guestbook']);
    },
  });
};

// Q&A API hooks
export const useQAndAInfinite = (pageSize = 50, anonymousUserId = null, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['qanda', 'infinite', pageSize, anonymousUserId],
    queryFn: async ({ pageParam = null }) => {
      const token = localStorage.getItem('weddingToken');
      const params = {
        limit: pageSize,
      };
      if (pageParam) {
        params.cursor = pageParam;
      }
      if (anonymousUserId) {
        params.anonymousUserId = anonymousUserId;
      }
      const response = await api.get('/questions', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return {
        items: response.data.items,
        nextCursor: response.data.nextCursor || null,
        hasMore: response.data.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    staleTime: 0, // Always fetch latest results - no caching
    gcTime: 0, // Don't keep in cache
    refetchOnWindowFocus: true, // Refetch when window regains focus to get latest
    ...options,
  });
};

export const useCreateQuestionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, question }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/questions', {
        name,
        question,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Optimistically add the new question to the cache
      queryClient.setQueryData(['qanda', 'infinite', 50], (oldData) => {
        if (!oldData) {
          // If no cache exists, create a new one with the question
          return {
            pages: [{
              items: [{ ...data, votes: 0, hasVoted: false }],
              nextCursor: null,
              hasMore: false,
            }],
            pageParams: [null],
          };
        }

        // Add the new question to the first page (newest questions appear first, but will be sorted by votes)
        const firstPage = oldData.pages[0];
        const updatedFirstPage = {
          ...firstPage,
          items: [{ ...data, votes: 0, hasVoted: false }, ...firstPage.items],
        };

        return {
          ...oldData,
          pages: [updatedFirstPage, ...oldData.pages.slice(1)],
        };
      });

      // Invalidate and refetch to ensure consistency with server (will be sorted by votes)
      queryClient.invalidateQueries(['qanda'], { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to create question:', error);
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['qanda']);
    },
  });
};

export const useVoteQuestionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ questionId, anonymousUserId }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post(`/questions/${questionId}/vote`, {
        anonymousUserId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update the question's vote count
      queryClient.setQueryData(['qanda', 'infinite', 50], (oldData) => {
        if (!oldData) return oldData;

        // Update all pages to find and update the question
        const updatedPages = oldData.pages.map(page => ({
          ...page,
          items: page.items.map(item => {
            if (item.id === variables.questionId) {
              return {
                ...item,
                votes: (item.votes || 0) + 1,
                hasVoted: true,
              };
            }
            return item;
          }),
        }));

        return {
          ...oldData,
          pages: updatedPages,
        };
      });

      // Invalidate and refetch to ensure consistency with server (will be re-sorted by votes)
      queryClient.invalidateQueries(['qanda'], { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to vote on question:', error);
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['qanda']);
    },
  });
};

export const useUnvoteQuestionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ questionId, anonymousUserId }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.delete(`/questions/${questionId}/vote`, {
        params: { anonymousUserId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update the question's vote count
      queryClient.setQueryData(['qanda', 'infinite', 50], (oldData) => {
        if (!oldData) return oldData;

        // Update all pages to find and update the question
        const updatedPages = oldData.pages.map(page => ({
          ...page,
          items: page.items.map(item => {
            if (item.id === variables.questionId) {
              return {
                ...item,
                votes: Math.max(0, (item.votes || 0) - 1),
                hasVoted: false,
              };
            }
            return item;
          }),
        }));

        return {
          ...oldData,
          pages: updatedPages,
        };
      });

      // Invalidate and refetch to ensure consistency with server (will be re-sorted by votes)
      queryClient.invalidateQueries(['qanda'], { refetchType: 'active' });
    },
    onError: (error) => {
      console.error('Failed to unvote on question:', error);
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['qanda']);
    },
  });
};

// Generate upload URL for a file
export const useUploadUrlMutation = () => {
  return useMutation({
    mutationFn: async ({ fileName, contentType }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/upload-url', {
        fileName,
        contentType,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Generate download URLs for multiple files
export const useDownloadUrlsMutation = () => {
  return useMutation({
    mutationFn: async ({ fileNames }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/download-urls', {
        fileNames,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Upload file to signed URL with progress tracking
export const uploadFile = async (url, file, onProgress, onSuccess) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Call success callback to invalidate caches
        if (onSuccess) {
          onSuccess();
        }
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

export default api;